<style>
@import "@/assets/css/custom.css";
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Courier Repayment" />

		<div class="container-fluid">
			<div v-if="loading" class="card">
				<div class="card-body text-center py-5">
					<span class="spinner-border spinner-border-sm me-2"></span>
					Loading courier details…
				</div>
			</div>

			<div v-else-if="loadError" class="card">
				<div class="card-body text-center py-5">
					<p class="text-danger mb-3">{{ loadError }}</p>
					<button type="button" class="btn btn-light" @click="$router.push({ name: 'courier_account' })">Back to courier list</button>
				</div>
			</div>

			<div v-else-if="courier" class="card">
				<div class="card-body">
					<div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
						<div>
							<h5 class="mb-1">Courier repayment #{{ courier.id }}</h5>
							<div class="small text-muted">
								<div><strong>Party:</strong> {{ userTypeLabel(courier.request_user_type) }} — {{ courier.request_user_name || "—" }}</div>
								<div><strong>Contact:</strong> {{ courier.request_user_contact || "—" }}</div>
								<div><strong>Total devices:</strong> {{ courier.total_devices ?? "—" }}</div>
								<div><strong>Invoice:</strong> {{ courier.invoice_number || "—" }}</div>
								<div><strong>Recorded:</strong> {{ courier.created_at ? $filters.formatDate(courier.created_at) : "—" }}</div>
							</div>
						</div>
						<button type="button" class="btn btn-light btn-sm" @click="$router.push({ name: 'courier_account' })">Back</button>
					</div>

					<div class="card border-light mb-4">
						<div class="card-body py-3">
							<div class="row text-center small">
								<div class="col-md-2">
									<span class="text-muted d-block">Grand total</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.grand_total) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Paid</span>
									<span class="fw-bold text-success">₹{{ formatMoney(summary?.paid_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Pending</span>
									<span class="fw-bold text-warning">₹{{ formatMoney(pendingAmount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Online paid</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.online_amount) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Cash paid</span>
									<span class="fw-bold">₹{{ formatMoney(summary?.cash_in_hand) }}</span>
								</div>
								<div class="col-md-2">
									<span class="text-muted d-block">Payment status</span>
									<span class="badge rounded-pill" :class="paymentStatusClass(summary?.payment_status)">
										{{ summary?.payment_status_label || paymentStatusLabel(summary?.payment_status) }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div class="mt-2 mb-4">
						<h6 class="border-bottom pb-2">Products</h6>
						<div class="table-responsive">
							<table class="table table-sm table-bordered align-middle">
								<thead class="table-light">
									<tr align="center">
										<th>Product</th>
										<th>Qty</th>
										<th>Amount (₹)</th>
										<th>GST (₹)</th>
										<th>Total (₹)</th>
										<th>Paid (₹)</th>
										<th>Pending (₹)</th>
										<th>Payment mode</th>
										<th>Bank / Payment ID</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="p in products" :key="'cp-' + p.id" align="center">
										<td class="text-start">{{ productTypeLabel(p.product_type) || p.product_type_label || "—" }}</td>
										<td>{{ p.quantity }}</td>
										<td>₹{{ formatMoney(p.amount) }}</td>
										<td>₹{{ formatMoney(p.gst) }}</td>
										<td>₹{{ formatMoney(p.total) }}</td>
										<td>₹{{ formatMoney(p.payment_transaction?.paid_amount) }}</td>
										<td>₹{{ formatMoney(p.payment?.pending_amount) }}</td>
										<td>{{ paymentModeLabel(p.payment?.payment_mode) }}</td>
										<td class="text-start small">
											<div>{{ p.payment?.bank_account?.bank_name || "—" }}</div>
											<div class="text-muted">{{ p.payment?.payment_id || "—" }}</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<div class="mt-2 mb-4">
						<h6 class="border-bottom pb-2">Payment logs</h6>
						<p v-if="!paymentLogs.length" class="text-muted mb-0">No payment logs for this courier.</p>
						<div v-else class="table-responsive">
							<table class="table table-sm table-bordered align-middle">
								<thead class="table-light">
									<tr align="center">
										<th>#</th>
										<th>Transaction amount (₹)</th>
										<th>Status</th>
										<th>Pending (₹)</th>
										<th>Credit (₹)</th>
										<th>Total (₹)</th>
										<th>Bank</th>
										<th>Payment ID</th>
										<th>Payment date</th>
										<th>Recorded on</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(log, idx) in paymentLogs" :key="'cpl-' + log.id" align="center">
										<td>{{ idx + 1 }}</td>
										<td>₹{{ formatMoney(log.transaction_amount) }}</td>
										<td>
											<span class="badge rounded-pill" :class="paymentStatusClass(log.status)">
												{{ log.status_label || paymentStatusLabel(log.status) }}
											</span>
										</td>
										<td>₹{{ formatMoney(log.pending_amount) }}</td>
										<td>₹{{ formatMoney(log.credit_amount) }}</td>
										<td>₹{{ formatMoney(log.total_amount) }}</td>
										<td class="text-start small">{{ log.bank_name || "—" }}</td>
										<td class="text-start small">{{ log.payment_id || "—" }}</td>
										<td>{{ log.payment_date ? $filters.formatDate(log.payment_date) : "—" }}</td>
										<td class="text-start small">{{ log.created_at ? $filters.formatDate(log.created_at) : "—" }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<form @submit.prevent="submit">
						<h6 class="border-bottom pb-2">Record repayment</h6>

						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Repayment amount (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0.01" step="0.01" v-model="form.amount" placeholder="0.00" />
								<small class="text-muted">Pending balance: ₹{{ formatMoney(pendingAmount) }}</small>
								<div v-if="errors.amount" class="text-danger small mt-1">{{ errors.amount }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.payment_type" @change="onPaymentTypeChange">
									<option value="">— Select —</option>
									<option value="1">Cash</option>
									<option value="2">Online</option>
									<option value="3">Both</option>
								</select>
								<div v-if="errors.payment_type" class="text-danger small mt-1">{{ errors.payment_type }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment date</label>
								<input class="form-control" type="date" v-model="form.payment_date" />
								<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
							</div>
						</div>

						<div class="row" v-if="form.payment_type === '2' || form.payment_type === '3'">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Bank account<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.bank_account_id">
									<option value="">— Select bank —</option>
									<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
								<small class="text-muted">Required for online and both payments.</small>
								<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Online amount (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_online" placeholder="0.00" />
								<div v-if="errors.cash_online" class="text-danger small mt-1">{{ errors.cash_online }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment / transaction ID</label>
								<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
								<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
							</div>
						</div>

						<div class="row" v-if="form.payment_type === '1' || form.payment_type === '3'">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Cash in hand (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_in_hand" placeholder="0.00" />
								<div v-if="errors.cash_in_hand" class="text-danger small mt-1">{{ errors.cash_in_hand }}</div>
							</div>
						</div>

						<div class="row" v-if="paymentBreakdown">
							<div class="col-md-12">
								<div class="card border-light mt-2">
									<div class="card-body py-2">
										<div class="row text-center small">
											<div class="col-md-3">
												<span class="text-muted d-block">Repayment amount</span>
												<span class="fw-bold">₹{{ paymentBreakdown.amount }}</span>
											</div>
											<div class="col-md-3" v-if="form.payment_type === '2' || form.payment_type === '3'">
												<span class="text-muted d-block">Online</span>
												<span class="fw-bold">₹{{ paymentBreakdown.online }}</span>
											</div>
											<div class="col-md-3" v-if="form.payment_type === '1' || form.payment_type === '3'">
												<span class="text-muted d-block">Cash</span>
												<span class="fw-bold">₹{{ paymentBreakdown.cash }}</span>
											</div>
											<div class="col-md-3">
												<span class="text-muted d-block">Match</span>
												<span class="fw-bold" :class="paymentBreakdown.statusClass">{{ paymentBreakdown.statusLabel }}</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-12 form-group mt-3">
								<label class="control-label">Remarks</label>
								<textarea class="form-control" rows="2" v-model="form.remarks" placeholder="Optional remarks"></textarea>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Submit repayment" }}
								</button>
								<button type="button" class="btn btn-light ms-2" :disabled="submitting" @click="$router.push({ name: 'courier_account' })">
									Cancel
								</button>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "CourierRepayment",
	data() {
		return {
			token: "",
			courierId: null,
			loading: true,
			loadError: "",
			submitting: false,
			errors: {},

			courier: null,
			products: [],
			paymentLogs: [],
			summary: null,
			bankAccounts: [],
			allProducts: [],

			form: {
				amount: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				payment_id: "",
				bank_account_id: "",
				payment_date: "",
				remarks: "",
			},
		};
	},
	computed: {
		pendingAmount() {
			if (this.summary?.pending_amount != null && this.summary.pending_amount !== "") {
				return parseFloat(this.summary.pending_amount) || 0;
			}
			const grand = parseFloat(this.summary?.grand_total) || 0;
			const paid = parseFloat(this.summary?.paid_amount) || 0;
			return Math.max(0, grand - paid);
		},
		paymentBreakdown() {
			if (!this.form.payment_type) return null;
			const amount = parseFloat(this.form.amount) || 0;
			const online = parseFloat(this.form.cash_online) || 0;
			const cash = parseFloat(this.form.cash_in_hand) || 0;
			let paid = 0;
			if (this.form.payment_type === "2") paid = online;
			else if (this.form.payment_type === "1") paid = cash;
			else if (this.form.payment_type === "3") paid = online + cash;
			else return null;
			const eps = 0.01;
			const diff = paid - amount;
			let statusLabel = "Partial";
			let statusClass = "text-warning";
			if (Math.abs(diff) < eps) {
				statusLabel = "Full";
				statusClass = "text-success";
			} else if (diff > eps) {
				statusLabel = "Over";
				statusClass = "text-info";
			}
			return {
				amount: amount.toFixed(2),
				online: online.toFixed(2),
				cash: cash.toFixed(2),
				statusLabel,
				statusClass,
			};
		},
	},
	created() {
		this.courierId = this.$route.params.courier_id;
		if (!this.courierId) {
			this.loading = false;
			this.loadError = "Courier ID is missing";
			return;
		}
		this.init();
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (staff.token) {
					this.token = staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			try {
				await Promise.all([this.fetchBankAccounts(), this.fetchAllProducts(), this.fetchCourierDetails()]);
			} catch (e) {
				this.loadError = e?.message || "Failed to load courier details";
			} finally {
				this.loading = false;
			}
		},

		fetchCourierDetails() {
			return this.$axios
				.get(`/staffs/courierDetails?courier_id=${encodeURIComponent(this.courierId)}`, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const data = res.data?.data || {};
					this.courier = data.courier || null;
					this.products = Array.isArray(data.products) ? data.products : [];
					this.paymentLogs = Array.isArray(data.payment_logs) ? data.payment_logs : [];
					this.summary = data.summary || null;
					if (!this.courier) {
						throw new Error("Courier not found");
					}
				})
				.catch((err) => {
					throw new Error(err.response?.data?.message || "Failed to load courier details");
				});
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		onPaymentTypeChange() {
			if (this.form.payment_type === "2") {
				this.form.cash_in_hand = "";
			} else if (this.form.payment_type === "1") {
				this.form.cash_online = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
			}
		},

		fetchAllProducts() {
			return this.$axios
				.get("/staffs/allProducts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.allProducts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.allProducts = [];
				});
		},

		productTypeLabel(t) {
			const product = this.allProducts.find((p) => String(p.id) === String(t));
			return product?.product_name || "";
		},

		userTypeLabel(t) {
			const map = { 1: "Dealer", 2: "Customer", 6: "Technician" };
			return map[String(t)] || "—";
		},

		paymentStatusLabel(v) {
			const map = { 1: "Received", 2: "Not received", 3: "Partially received" };
			return map[Number(v)] || "—";
		},

		paymentStatusClass(v) {
			const n = Number(v);
			if (n === 1) return "bg-success";
			if (n === 2) return "bg-danger";
			if (n === 3) return "bg-warning";
			return "bg-secondary";
		},

		paymentModeLabel(v) {
			const map = { 1: "Cash", 2: "Online", 3: "Both" };
			return map[Number(v)] || "—";
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		validate() {
			this.errors = {};
			const pending = this.pendingAmount;
			const amount = parseFloat(this.form.amount);

			if (isNaN(amount) || amount <= 0) {
				this.errors.amount = "Enter a valid repayment amount";
				return false;
			}
			if (amount > pending) {
				this.errors.amount = `Amount cannot exceed pending balance (₹${this.formatMoney(pending)})`;
				return false;
			}

			if (!this.form.payment_type) {
				this.errors.payment_type = "Select payment type";
				return false;
			}

			const pt = this.form.payment_type;
			const eps = 0.01;

			if (pt === "2" || pt === "3") {
				if (!this.form.bank_account_id) {
					this.errors.bank_account_id = "Select bank account";
					return false;
				}
				const co = parseFloat(this.form.cash_online);
				if (isNaN(co) || co <= 0) {
					this.errors.cash_online = "Enter a valid online amount";
					return false;
				}
			}

			if (pt === "1" || pt === "3") {
				const ch = parseFloat(this.form.cash_in_hand);
				if (isNaN(ch) || ch <= 0) {
					this.errors.cash_in_hand = "Enter a valid cash in hand amount";
					return false;
				}
			}

			if (pt === "1") {
				const ch = parseFloat(this.form.cash_in_hand);
				if (Math.abs(ch - amount) > eps) {
					this.errors.cash_in_hand = "Cash amount must match repayment amount";
					return false;
				}
			} else if (pt === "2") {
				const co = parseFloat(this.form.cash_online);
				if (Math.abs(co - amount) > eps) {
					this.errors.cash_online = "Online amount must match repayment amount";
					return false;
				}
			} else if (pt === "3") {
				const co = parseFloat(this.form.cash_online) || 0;
				const ch = parseFloat(this.form.cash_in_hand) || 0;
				if (Math.abs(co + ch - amount) > eps) {
					this.errors.amount = "Online + cash must equal repayment amount";
					return false;
				}
			}

			return true;
		},

		buildPayload() {
			const pt = this.form.payment_type;
			const payload = {
				courier_id: parseInt(this.courierId, 10),
				amount: parseFloat(this.form.amount),
				payment_type: parseInt(pt, 10),
				cash_online: null,
				cash_in_hand: null,
				payment_id: String(this.form.payment_id || "").trim() || null,
				bank_account_id: null,
				payment_date: String(this.form.payment_date || "").trim() || null,
				remarks: String(this.form.remarks || "").trim() || null,
			};

			if (pt === "2" || pt === "3") {
				payload.cash_online = parseFloat(this.form.cash_online);
				payload.bank_account_id = parseInt(this.form.bank_account_id, 10);
			}
			if (pt === "1" || pt === "3") {
				payload.cash_in_hand = parseFloat(this.form.cash_in_hand);
			}

			return payload;
		},

		async submit() {
			if (this.submitting) return;
			if (!this.validate()) return;

			this.submitting = true;
			this.getToken();
			try {
				const payload = this.buildPayload();
				const res = await this.$axios.post("/staffs/courierRepayment", payload, {
					headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (ok) {
					this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Repayment recorded", "success");
					this.$router.push({ name: "courier_account" });
				} else {
					this.$applyApiFieldErrors(this.errors, res.data);
					this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to record repayment"), "error");
				}
			} catch (err) {
				this.$applyApiFieldErrors(this.errors, err);
				this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>
